import React, { useState, useRef, useEffect } from 'react';
import { useCurrency, Currency } from '../context/CurrencyContext';
import { useTranslation } from '../context/TranslationContext';
import { ChevronDown } from 'lucide-react';
import '../styles/CurrencySelector.css';

interface CurrencySelectorProps {
  isInDropdown?: boolean;
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({ isInDropdown = false }) => {
  const { selectedCurrency, setSelectedCurrency, currencySymbol } = useCurrency();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const currencies: { code: Currency; name: string; symbol: string }[] = [
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
    { code: 'CHF', name: 'Swiss Franc', symbol: '₣' },
    { code: 'GBP', name: 'British Pound', symbol: '£' }
  ];

  const handleCurrencySelect = (currency: Currency) => {
    setSelectedCurrency(currency);
    setIsOpen(false);
  };

  const currentCurrency = currencies.find(c => c.code === selectedCurrency);

  return (
    <div className={`currency-selector ${isInDropdown ? 'in-dropdown' : ''}`} ref={dropdownRef}>
      <button 
        className={`currency-selector-button ${isInDropdown ? 'dropdown-style' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={t('common.select_currency')}
      >
        <span className="currency-symbol">{currentCurrency?.symbol}</span>
        <span className="currency-code">{currentCurrency?.code}</span>
        <ChevronDown className={`currency-arrow ${isOpen ? 'open' : ''}`} size={16} />
      </button>
      
      {isOpen && (
        <div className={`currency-dropdown ${isInDropdown ? 'dropdown-position' : ''}`}>
          {currencies.map((currency) => (
            <button
              key={currency.code}
              className={`currency-option ${selectedCurrency === currency.code ? 'selected' : ''}`}
              onClick={() => handleCurrencySelect(currency.code)}
            >
              <span className="currency-symbol">{currency.symbol}</span>
              <span className="currency-name">{currency.name}</span>
              <span className="currency-code">{currency.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
